import React from 'react'
import { ThemeProvider } from '@mui/material/styles'
import CssBaseline from '@mui/material/CssBaseline'
import { RouterProvider } from 'react-router5'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'

import createRouter from './system/router'
import { RouterContextProvider } from './contexts/router'
import { SnackbarContextProvider } from './contexts/snackbar'
import { LoadingContextProvider } from './contexts/loading'
import { AccountContextProvider, useAccount } from './contexts/account'
import Layout from './pages/Layout'
import routes from './routes'
import theme from './theme'
import useRouter from './hooks/useRouter'

const queryClient = new QueryClient()
const AppRouter = createRouter(routes)
const { router, RenderPage } = AppRouter
router.start()

function RouteGuard({ children }: { children: React.ReactNode }) {
  const { user, initialized } = useAccount()
  const { name, navigate } = useRouter()

  const route = routes.find(r => r.name === name)
  const isPublic = route?.meta?.public === true

  React.useEffect(() => {
    if (initialized && !isPublic && !user) {
      navigate('login')
    }
  }, [initialized, isPublic, user, name])

  if (!initialized) return null
  if (!isPublic && !user) return null

  return <>{children}</>
}

function AppContent() {
  const { name } = useRouter()
  const route = routes.find(r => r.name === name)
  const isPublic = route?.meta?.public === true

  if (isPublic) {
    return <RenderPage />
  }

  return (
    <Layout>
      <RenderPage />
    </Layout>
  )
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <RouterProvider router={router}>
          <RouterContextProvider router={AppRouter}>
            <SnackbarContextProvider>
              <LoadingContextProvider>
                <AccountContextProvider>
                  <RouteGuard>
                    <AppContent />
                  </RouteGuard>
                </AccountContextProvider>
              </LoadingContextProvider>
            </SnackbarContextProvider>
          </RouterContextProvider>
        </RouterProvider>
      </ThemeProvider>
    </QueryClientProvider>
  )
}
